import { Col, DatePicker, Form, Row } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import type { Dayjs } from "dayjs"
import React, { useCallback, useRef } from "react"

import { useDateFormat } from "../../../../../hooks/use-date-format.ts"
import {
  type ScheduleTriple,
  withDuration,
  withEndDate,
  withStartDate
} from "../../../shared/duration-date-calculator.ts"
import { type TemplateDocumentFormData } from "../../../types/template-document-form-data.tsx"
import { DurationFormItem } from "../DurationFormItem.tsx"
import { TextArea } from "../../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

interface ProjectOverviewBlockProps {
  form: FormInstance<TemplateDocumentFormData>
}

/**
 * Start Date / Completion Date / Duration are one bidirectional trio here,
 * powered by the shared `duration-date-calculator.ts` engine — editing any
 * one recalculates at most one of the other two, last-edit-wins, exactly the
 * `PaymentScheduleBlock.tsx` Amount/Percentage pattern (an `isUpdatingRef` +
 * `setTimeout(…, 0)` guard so a programmatic `form.setFieldsValue` call never
 * re-triggers another field's handler). This supersedes the old narrow
 * `dateAutoFill` wiring (copying Start into an empty Completion) — that
 * behaviour is now one case of the general "two of three ⇒ the third" rule,
 * not a separate mechanism stacked alongside it.
 */
export function ProjectOverviewBlock({ form }: ProjectOverviewBlockProps) {
  const { getDateValueProps, createDateRangeRules, normalizeDate } =
    useDateFormat()

  const isUpdatingRef = useRef(false)

  const currentSchedule = useCallback(
    (): ScheduleTriple => ({
      startDate: form.getFieldValue("start_date") ?? null,
      endDate: form.getFieldValue("completion_date") ?? null,
      duration: form.getFieldValue("duration") ?? null,
      durationUnit: form.getFieldValue("duration_type") ?? null
    }),
    [form]
  )

  const runUpdate = useCallback((apply: () => void) => {
    isUpdatingRef.current = true
    apply()
    setTimeout(() => {
      isUpdatingRef.current = false
    }, 0)
  }, [])

  const handleStartDateChange = useCallback(
    (value: Dayjs | null) => {
      if (isUpdatingRef.current) return

      const schedule = currentSchedule()
      const derived = withStartDate(schedule, normalizeDate(value))
      if (derived.duration == null) return

      runUpdate(() => {
        form.setFieldsValue({
          duration: String(derived.duration),
          duration_type: derived.durationUnit ?? "days"
        })
      })
    },
    [currentSchedule, form, normalizeDate, runUpdate]
  )

  const handleCompletionDateChange = useCallback(
    (value: Dayjs | null) => {
      if (isUpdatingRef.current) return

      const schedule = currentSchedule()
      const derived = withEndDate(schedule, normalizeDate(value))
      if (derived.duration == null) return

      runUpdate(() => {
        form.setFieldsValue({
          duration: String(derived.duration),
          duration_type: derived.durationUnit ?? "days"
        })
      })
    },
    [currentSchedule, form, normalizeDate, runUpdate]
  )

  const applyDerivedDuration = useCallback(
    (duration: number | null, durationUnit: string | null) => {
      const schedule = currentSchedule()
      const derived = withDuration(schedule, duration, durationUnit)

      if ((derived.endDate ?? null) !== (schedule.endDate ?? null)) {
        runUpdate(() => {
          form.setFieldsValue({ completion_date: derived.endDate ?? null })
        })
        return
      }

      if ((derived.startDate ?? null) !== (schedule.startDate ?? null)) {
        runUpdate(() => {
          form.setFieldsValue({ start_date: derived.startDate ?? null })
        })
      }
    },
    [currentSchedule, form, runUpdate]
  )

  const handleDurationValueChange = useCallback(
    (value: number | null) => {
      if (isUpdatingRef.current) return
      applyDerivedDuration(value, form.getFieldValue("duration_type"))
    },
    [applyDerivedDuration, form]
  )

  const handleDurationUnitChange = useCallback(
    (unit: string) => {
      if (isUpdatingRef.current) return

      const rawDuration = form.getFieldValue("duration")
      const duration =
        rawDuration === "" || rawDuration == null ? null : Number(rawDuration)
      if (duration == null || !Number.isFinite(duration)) return

      applyDerivedDuration(duration, unit)
    },
    [applyDerivedDuration, form]
  )

  return (
    <>
      <Row gutter={[12, 0]}>
        <Col span={12}>
          <Form.Item<TemplateDocumentFormData>
            name={["project_type"]}
            label="Project Type"
            rules={[
              {
                required: false,
                whitespace: true,
                message: "Please input project type"
              }
            ]}
          >
            <TextArea disabled placeholder="Enter project type" autoSize />
          </Form.Item>
        </Col>
        <Col span={12}>
          <DurationFormItem<TemplateDocumentFormData>
            durationName={["duration"]}
            durationDependencies={[["start_date"], ["completion_date"]]}
            typeName={["duration_type"]}
            formItemProps={{ label: "Duration" }}
            onValueChange={handleDurationValueChange}
            onUnitChange={handleDurationUnitChange}
          />
        </Col>
        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={["start_date"]}
            label="Start Date"
            dependencies={[["completion_date"]]}
            getValueProps={getDateValueProps}
            rules={createDateRangeRules<TemplateDocumentFormData>(
              "completion_date",
              "start"
            )}
          >
            <DatePicker className="w-full" onChange={handleStartDateChange} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={["completion_date"]}
            label="Completion Date"
            dependencies={[["start_date"]]}
            getValueProps={getDateValueProps}
          >
            <DatePicker
              className="w-full"
              onChange={handleCompletionDateChange}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={["prepared_by"]}
            label="Prepared By"
          >
            <TextInput disabled placeholder="Enter prepared by" />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item<TemplateDocumentFormData>
            name={["summary"]}
            label="Summary"
          >
            <TextArea rows={3} placeholder="Enter summary..." />
          </Form.Item>
        </Col>
      </Row>
    </>
  )
}
